import { useState } from 'react';
import { Box, Chip, IconButton, MenuItem, Stack, TextField, Tooltip } from '@mui/material';
import BoltIcon from '@mui/icons-material/Bolt';
import { type GridColDef } from '@mui/x-data-grid';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { PageHeader } from '../../../shared/components/PageHeader';
import { DataTable, type DataTableQuery } from '../../../shared/components/DataTable';
import { GRID_PAGE_SIZE, usePersistedGridState } from '../../../shared/grid/usePersistedGridState';
import { ColaboradorSelect } from '../../../shared/components/ColaboradorSelect';
import { ErrorState } from '../../../shared/components/ErrorState';
import { describeApiError } from '../../../shared/api/errors';
import { valesGlobalApi } from './api';
import { ValeAcoesDialog } from './ValeAcoesDialog';
import type { ValeGlobalListItem, ValeStatus } from './types';

const DEFAULT_QUERY: DataTableQuery = {
  page: 1,
  pageSize: GRID_PAGE_SIZE,
  sortBy: 'dataSolicitacao',
  sortOrder: 'desc',
};

const STATUSES: ValeStatus[] = ['ABE', 'PAG', 'CAN'];

function formatMoney(v: number | string): string {
  const n = typeof v === 'number' ? v : Number(v);
  return (Number.isFinite(n) ? n : 0).toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  });
}
function formatDate(iso: string): string {
  const d = dayjs(iso);
  return d.isValid() ? d.format('DD/MM/YYYY') : iso;
}
function statusColor(s: ValeStatus): 'success' | 'default' | 'warning' {
  if (s === 'PAG') return 'success';
  if (s === 'CAN') return 'default';
  return 'warning';
}

/**
 * Visão global de Vales (administrativo) — grid de todos os colaboradores com
 * filtros colaborador + status. A gestão (parcelas/baixa/editar/cancelar) abre o
 * pop-up `ValeAcoesDialog` do vale selecionado na linha. Listagem via
 * `valesGlobalApi` (`ApiEmployeeValeListAll`, card [LfcrbNrv]/ATD-0055).
 */
export function ValesGlobalPage(): JSX.Element {
  const { t } = useTranslation();
  const {
    query,
    setQuery,
    filters,
    patchFilters,
    clear,
  } = usePersistedGridState('vales-global', DEFAULT_QUERY, {
    colaboradorCodigo: null as number | null,
    status: '' as ValeStatus | '',
  });
  const colabFiltro = filters.colaboradorCodigo;
  const statusFiltro = filters.status;
  const setColabFiltro = (id: number | null) => patchFilters({ colaboradorCodigo: id });
  const setStatusFiltro = (status: ValeStatus | '') => patchFilters({ status });
  const [acoesFor, setAcoesFor] = useState<ValeGlobalListItem | null>(null);

  const listQuery = useQuery({
    queryKey: ['vales', 'global', query, colabFiltro, statusFiltro],
    queryFn: () =>
      valesGlobalApi.list(query, { colaboradorCodigo: colabFiltro, status: statusFiltro }),
    placeholderData: (previous) => previous,
  });

  const columns: GridColDef<ValeGlobalListItem>[] = [
    {
      field: 'colaboradorNome',
      headerName: t('features.colaboradores.valesGlobal.columns.colaborador'),
      flex: 1,
      minWidth: 200,
    },
    {
      field: 'dataSolicitacao',
      headerName: t('features.colaboradores.vales.columns.dataSolicitacao'),
      width: 150,
      valueFormatter: (value: string) => formatDate(value),
    },
    {
      field: 'valor',
      headerName: t('features.colaboradores.vales.columns.valor'),
      width: 140,
      align: 'right',
      headerAlign: 'right',
      valueFormatter: (value: number) => formatMoney(value),
    },
    {
      field: 'nroParcelas',
      headerName: t('features.colaboradores.vales.columns.nroParcelas'),
      width: 110,
      align: 'right',
      headerAlign: 'right',
      sortable: false,
    },
    {
      field: 'status',
      headerName: t('features.colaboradores.vales.columns.status'),
      width: 130,
      sortable: false,
      renderCell: (params) => (
        <Chip
          size="small"
          label={t(`features.colaboradores.vales.status.${params.row.status}`)}
          color={statusColor(params.row.status)}
          variant={params.row.status === 'CAN' ? 'outlined' : 'filled'}
        />
      ),
    },
    {
      field: 'actions',
      headerName: '',
      width: 70,
      sortable: false,
      filterable: false,
      align: 'right',
      headerAlign: 'right',
      renderCell: (params) => (
        <Tooltip title={t('features.colaboradores.valesGlobal.actions.acoes')}>
          <IconButton
            size="small"
            aria-label={t('features.colaboradores.valesGlobal.actions.acoesAria', {
              nome: params.row.colaboradorNome,
            })}
            onClick={() => setAcoesFor(params.row)}
          >
            <BoltIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  if (listQuery.isLoadingError) {
    return (
      <ErrorState
        description={describeApiError(listQuery.error)}
        onRetry={() => listQuery.refetch()}
      />
    );
  }

  return (
    <Box>
      <PageHeader
        title={t('features.colaboradores.valesGlobal.title')}
        subtitle={t('features.colaboradores.valesGlobal.subtitle')}
      />
      <Stack spacing={2}>
        <DataTable<ValeGlobalListItem>
          rows={listQuery.data?.data ?? []}
          columns={columns}
          total={listQuery.data?.meta.total ?? 0}
          loading={listQuery.isLoading || listQuery.isFetching}
          query={query}
          onQueryChange={setQuery}
          onClear={clear}
          searchPlaceholder={t('features.colaboradores.valesGlobal.searchPlaceholder')}
          filters={
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 1.5,
                alignItems: 'center',
                flex: 1,
              }}
            >
              <Box sx={{ width: 240 }}>
                <ColaboradorSelect
                  value={colabFiltro}
                  onChange={(id) => setColabFiltro(id)}
                  label={t('features.colaboradores.valesGlobal.filters.colaborador')}
                  placeholder={t('features.colaboradores.valesGlobal.filters.colaboradorTodos')}
                />
              </Box>
              <TextField
                select
                size="small"
                label={t('features.colaboradores.valesGlobal.filters.status')}
                value={statusFiltro}
                onChange={(e) => setStatusFiltro(e.target.value as ValeStatus | '')}
                sx={{ width: 190 }}
              >
                <MenuItem value="">
                  {t('features.colaboradores.valesGlobal.filters.statusTodos')}
                </MenuItem>
                {STATUSES.map((s) => (
                  <MenuItem key={s} value={s}>
                    {t(`features.colaboradores.vales.status.${s}`)}
                  </MenuItem>
                ))}
              </TextField>
            </Box>
          }
        />
      </Stack>

      {acoesFor && (
        <ValeAcoesDialog
          open={acoesFor !== null}
          vale={acoesFor}
          onClose={() => setAcoesFor(null)}
        />
      )}
    </Box>
  );
}
